/**
 * BadgeIcons — len NOVÉ ikony pre odznaky + mapovanie odznak → ikona.
 *
 * Existujúce odznaky používajú ikony, ktoré už v BuzerIcons sú (flame, dumbbell,
 * mountain, check, people) — tu sa len referencujú menom, neduplikujú sa.
 *
 * NOVÉ ikony (sprout, owl, coins, gem, butterfly) pridaj do `ICONS` v BuzerIcons.tsx,
 * napr. hneď za posledný blok:
 *
 *     import { NEW_BADGE_ICONS } from "./BadgeIcons";
 *     Object.assign(ICONS, NEW_BADGE_ICONS);   // ICONS je interný register v BuzerIcons
 *
 * (alebo skopíruj 5 položiek nižšie priamo do literálu ICONS).
 * Potom funguje <BuzerIcon name="sprout" /> aj všetky badge ikony jednotne.
 *
 * Štýl je rovnaký ako zvyšok sady: 24×24, stroke = currentColor, round cap/join,
 * takže farbu zdedia z rodiča (čierna / téma / coral / zlatá…).
 */

export const NEW_BADGE_ICONS: Record<string, { label: string; cat: string; svg: string }> = {
  // v30.3 — zberateľské odznaky za ozdoby na hlavu (Prvé tri kúsky → Zberateľ → Šialený klobučník).
  hat: {
    label: "Klobúk",
    cat: "socialne",
    svg:
      '<path d="M7.8 13.6 C7.4 6.2 16.6 6.2 16.2 13.6"/>' +
      '<path d="M4 15.9 C8 13.7 16 13.7 20 15.9 C16 18.1 8 18.1 4 15.9Z"/>' +
      '<path d="M8.3 12.6 C10.6 13.6 13.4 13.6 15.7 12.6"/>',
  },
  wizardhat: {
    label: "Čarodejnícky klobúk",
    cat: "socialne",
    // špic sa hore stáča doľava a previsne — rovnaká silueta ako ozdoba `flair_wizard`
    svg:
      '<path d="M9.8 14.2 C10 9.4 11.2 6 13.2 3.6 C14 6 14.3 9.8 14.2 14.2Z"/>' +
      '<path d="M13.2 3.6 C11.7 2.4 9.8 2.9 9.2 4.5"/>' +
      '<path d="M4 16.4 C8 14 16 14 20 16.4 C16 18.8 8 18.8 4 16.4Z"/>' +
      '<path d="M9.8 13.4 C11.2 14.2 13 14.2 14.2 13.4"/>',
  },
  sprout: {
    label: "Klíček",
    cat: "priroda",
    svg:
      '<path d="M6 20 h12"/>' +
      '<path d="M12.5 20 C12.5 16 12.5 14 13.5 12"/>' +
      '<path d="M13.5 13 C16.5 13 18.5 11 18.5 7 C15 7.3 13.5 10 13.5 13Z"/>' +
      '<path d="M11.5 16 C8.5 16 6.5 14.5 6.5 11 C9.5 11 11.5 12.5 11.5 16Z"/>',
  },
  owl: {
    label: "Sova",
    cat: "priroda",
    svg:
      '<path d="M5 11 C5 6 8 4 12 4 C16 4 19 6 19 11 C19 16 16 20 12 20 C8 20 5 16 5 11Z"/>' +
      '<path d="M7.6 5 L7 2.4 L9.2 4.3"/><path d="M16.4 5 L17 2.4 L14.8 4.3"/>' +
      '<circle cx="9.3" cy="10" r="2.1"/><circle cx="14.7" cy="10" r="2.1"/>' +
      '<circle cx="9.3" cy="10" r="0.5" fill="currentColor" stroke="none"/>' +
      '<circle cx="14.7" cy="10" r="0.5" fill="currentColor" stroke="none"/>' +
      '<path d="M11 12.2 L12 13.6 L13 12.2"/>',
  },
  coins: {
    label: "Mince",
    cat: "praca",
    svg:
      '<ellipse cx="12" cy="8" rx="5.5" ry="2.3"/>' +
      '<path d="M6.5 8 V11 C6.5 12.3 9 13.3 12 13.3 C15 13.3 17.5 12.3 17.5 11 V8"/>' +
      '<path d="M6.5 11.5 V14.5 C6.5 15.8 9 16.8 12 16.8 C15 16.8 17.5 15.8 17.5 14.5 V11.5"/>',
  },
  gem: {
    label: "Drahokam",
    cat: "praca",
    svg:
      '<path d="M5 9 L8.5 4 H15.5 L19 9 L12 20.5 Z"/>' +
      '<line x1="5" y1="9" x2="19" y2="9"/>' +
      '<path d="M8.5 4 L10.5 9 L12 20.5 L13.5 9 L15.5 4"/>',
  },
  // v32 — rodina „Rovnováha" (odznaky za počet životných kategórií splnených v jeden deň).
  // Jeden tvar lupeňa, len ho pribúda: 2 → 4 → 6 → 8. Priamo zrkadlí petal-wheel kategórií
  // v appke, takže ikona povie, čo odznak meria, bez čítania popisu. Lupeň je pootočená kópia
  // toho istého `path` (rotate okolo stredu 12 12) — nič sa neduplikuje ručne, len uhly.
  // Najvyšší stupeň má navyše plný stred = kvet je kompletný.
  petals2: {
    label: "Dva lupene",
    cat: "priroda",
    svg:
      '<path d="M12 12 C8.6 9.5 8.6 5.3 12 3.6 C15.4 5.3 15.4 9.5 12 12Z" transform="rotate(315 12 12)"/>' +
      '<path d="M12 12 C8.6 9.5 8.6 5.3 12 3.6 C15.4 5.3 15.4 9.5 12 12Z" transform="rotate(45 12 12)"/>',
  },
  petals4: {
    label: "Štyri lupene",
    cat: "priroda",
    svg:
      '<path d="M12 12 C8.4 9.4 8.4 5.2 12 3.5 C15.6 5.2 15.6 9.4 12 12Z"/>' +
      '<path d="M12 12 C8.4 9.4 8.4 5.2 12 3.5 C15.6 5.2 15.6 9.4 12 12Z" transform="rotate(90 12 12)"/>' +
      '<path d="M12 12 C8.4 9.4 8.4 5.2 12 3.5 C15.6 5.2 15.6 9.4 12 12Z" transform="rotate(180 12 12)"/>' +
      '<path d="M12 12 C8.4 9.4 8.4 5.2 12 3.5 C15.6 5.2 15.6 9.4 12 12Z" transform="rotate(270 12 12)"/>',
  },
  petals6: {
    label: "Šesť lupeňov",
    cat: "priroda",
    svg:
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z"/>' +
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z" transform="rotate(60 12 12)"/>' +
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z" transform="rotate(120 12 12)"/>' +
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z" transform="rotate(180 12 12)"/>' +
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z" transform="rotate(240 12 12)"/>' +
      '<path d="M12 12 C9.0 9.4 9.0 5.2 12 3.5 C15.0 5.2 15.0 9.4 12 12Z" transform="rotate(300 12 12)"/>',
  },
  petals8: {
    label: "Osem lupeňov",
    cat: "priroda",
    svg:
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(45 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(90 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(135 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(180 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(225 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(270 12 12)"/>' +
      '<path d="M12 12 C9.7 9.4 9.7 5.2 12 3.5 C14.3 5.2 14.3 9.4 12 12Z" transform="rotate(315 12 12)"/>' +
      '<circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none"/>',
  },
  butterfly: {
    label: "Motýľ",
    cat: "priroda",
    svg:
      '<path d="M12 7 V16.3"/>' +
      '<path d="M12 9 C11 5 5 4 4 8 C3.3 11 7 11.5 12 12"/>' +
      '<path d="M12 12 C7 12.5 3.5 14 4 17.5 C4.5 20 9.5 19 12 14.5"/>' +
      '<path d="M12 9 C13 5 19 4 20 8 C20.7 11 17 11.5 12 12"/>' +
      '<path d="M12 12 C17 12.5 20.5 14 20 17.5 C19.5 20 14.5 19 12 14.5"/>' +
      '<path d="M12 7 C11.2 5.6 10.3 5.1 9.4 4.9"/>' +
      '<path d="M12 7 C12.8 5.6 13.7 5.1 14.6 4.9"/>',
  },
};

/**
 * Odznak (`code`) → názov ikony v BuzerIcons (po merge NEW_BADGE_ICONS).
 *   ✔ = existujúca v BuzerIcons        ✦ = nová (z NEW_BADGE_ICONS vyššie)
 */
export const BADGE_ICON: Record<string, string> = {
  first:      "sprout",     // 🌱 Prvý krok
  streak7:    "flame",      // 🔥 Týždeň v kuse
  streak30:   "dumbbell",   // 💪 Mesiac disciplíny
  streak100:  "mountain",   // 🏔️ Stovkár
  done100:    "check",      // ✅ Sto splnení
  done500:    "owl",        // 🦉 Päťstovka
  pts1000:    "coins",      // 💰 Tisícka
  pts5000:    "gem",        // 💎 Boháč
  social3:    "people",     // 🤝 Spoločenský
  social10:   "butterfly",  // 🦋 Motýľ
  tutorial:   "star2",      // 🎓 Tutorial expert
  // v26 §2 — nové tiery (ikony z existujúcej sady)
  done25:     "shoe",       // Rozbeh
  done250:    "footprints", // V rytme
  done1000:   "medal",      // Maratónec
  done2500:   "medal3",     // Legenda
  streak3:    "candle",     // Prvá iskra
  streak14:   "sunrise",    // Dva týždne v laufe
  streak60:   "shield",     // Železná vôľa
  streak180:  "hourglass",  // Polročný ťah
  streak365:  "sun",        // Rok bez prestávky
  pts15000:   "chart",      // Zberateľ bodov
  pts50000:   "briefcase",  // Bodový magnát
  pts150000:  "crown",      // Bodová dynastia
  chal1:      "flag",       // Prvá výzva
  chal3:      "target",     // Trojka v cieli
  chal10:     "compass",    // Výzvista
  chal25:     "puzzle",     // Kráľ výziev
  chalmaker:  "bulb",       // Zakladateľ
  comp1:      "stopwatch",  // Súťažiaci
  comp5:      "ball",       // Stály hráč
  comp15:     "bike",       // Matador
  win1:       "trophy",     // Prvé zlato
  win3:       "medal2",     // Zbierka trofejí
  win10:      "crown",      // Šampión
  social1:    "user",       // Prvý parťák
  social25:   "party",      // Duša partie
  mood7:      "smile",      // Týždeň pocitov
  mood30:     "journal",    // Náladomer
  mood100:    "brain",      // Sebapoznanie
  mood365:    "bookopen",   // Ročný denník
  plan10:     "clipboard",  // Organizátor
  plan50:     "pen",        // Plánovač
  plan200:    "calendar",   // Majster kalendára
  pw1:        "thumbsup",   // Perfektný týždeň
  pw5:        "sparkles",   // Päť z piatich
  pw20:       "flower",     // Sezóna bez chyby
  pw52:       "gift",       // Perfektný rok
  // v35 — týždenná konzistencia (6/7 a 7/7 dní, 52 zlatých týždňov)
  wsilver:    "medal2",     // Strieborný týždeň
  wgold:      "medal",      // Zlatý týždeň
  wgold52:    "crown",      // Zlatý rok
  // v32 — Rovnováha (koľko životných kategórií v jeden deň): pribúdajúce lupene
  lifecat2:   "petals2",    // ✦ Prvé lupene
  lifecat4:   "petals4",    // ✦ Pestrý deň
  lifecat6:   "petals6",    // ✦ Široký záber
  lifecat8:   "petals8",    // ✦ Kvet života
  loyal30:    "leaf",       // Mesiac s nami
  loyal90:    "herb",       // Štvrťrok
  loyal180:   "tree",       // Polrok vernosti
  loyal365:   "cake",       // Výročie
  comeback:   "wave",       // Návrat
  // v26 — Špeciálne (event/easter-egg achievementy)
  xmas:       "snow",       // Vianoce s Buzerlístkom
  newyear:    "rocket",     // Predsavzatie v akcii
  friday13:   "paw",        // Piatok trinásteho
  leapday:    "night",      // Prestupný skok
  bigday:     "clock",      // Desať v jednom
  century:    "pulse",      // Stovkový deň
  owl:        "moon",       // Nočná sova
  earlybird:  "bird",       // Ranné vtáča
  egg:        "egg",        // Pátrač (easter egg)
  // v30.3 — zberateľské (ozdoby na hlavu): trblietky → klobúk → čarodejnícky klobúk
  hats3:      "sparkles",   // Prvé tri kúsky
  hats10:     "hat",        // ✦ Zberateľ ozdôb
  hatsall:    "wizardhat",  // ✦ Šialený klobučník
};

/**
 * Render odznaku:
 *   import { BuzerIcon } from "./BuzerIcons";
 *   import { BADGE_ICON } from "./BadgeIcons";
 *   <BuzerIcon name={BADGE_ICON[badge.code]} size={28} />
 *
 * --- Pre backend (app.py BADGES): nahraď pole "icon" emoji za názov ikony ---
 * BADGE_ICON_PY = {
 *   "first": "sprout", "streak7": "flame", "streak30": "dumbbell",
 *   "streak100": "mountain", "done100": "check", "done500": "owl",
 *   "pts1000": "coins", "pts5000": "gem", "social3": "people", "social10": "butterfly",
 * }
 */
